import {
  alpha,
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Container,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, Edit, PersonOutline } from "@mui/icons-material";
import FormActions from "components/FormActions";
import ChildDietaryCard from "components/ChildDietaryCard";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getClassrooms,
  getParents as getParentsApi,
  updateChild as updateChildApi,
} from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/fr";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { stringAvatar } from "utils";
import languages from "components/languages";
import { FormFields } from "types";

type Classroom = { id: number; name: string; nurseryId: number };
type Parent = { id: number; firstName: string; lastName: string };

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const req = (label: string) => (
  <>
    {label} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const EditChild: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state as FormFields;
  const sanitized = item
    ? Object.fromEntries(
        Object.entries(item).map(([k, v]) => [
          k,
          v == null || v === "null" ? "" : v,
        ]),
      )
    : {};
  const { register, handleSubmit, setValue } = useForm<FormFields>({
    defaultValues: sanitized as FormFields,
  });
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState<File>();
  const [dob, setDob] = useState<Dayjs | null>(
    item?.dateOfBirth ? dayjs(item.dateOfBirth) : dayjs(),
  );
  const [parentId, setParentId] = useState(String(item?.parentId || ""));
  const [classroomId, setClassroomId] = useState(
    String(item?.classroomId || ""),
  );
  const [parents, setParents] = useState<Parent[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);

  useEffect(() => {
    Promise.all([getParentsApi(), getClassrooms()]).then(([pa, cl]) => {
      setParents(pa || []);
      setClassrooms(cl || []);
    });
  }, []);

  useEffect(() => {
    if (photo) setValue("photo", photo);
  }, [photo, setValue]);

  if (!item) {
    navigate("/child");
    return null;
  }

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    setSubmitting(true);
    const payload = {
      info: {
        ...data,
        id: item.id,
        nurseryId: classrooms[0]?.nurseryId,
      },
      parentId,
      classroomId,
      dateOfBirth: dob?.toISOString() || item.dateOfBirth,
    };

    updateChildApi(payload)
      .then(() => {
        enqueueSnackbar(t("child.updated"), { variant: "success" });
        navigate("/child");
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Failed", {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/child")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <PersonOutline sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("child.edit")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Photo */}
          <Box display="flex" justifyContent="center" mb={3}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Fab
                  size="small"
                  component="label"
                  color="primary"
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                >
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.length) setPhoto(e.target.files[0]);
                    }}
                  />
                  <Edit sx={{ fontSize: 16 }} />
                </Fab>
              }
            >
              <Avatar
                src={
                  photo
                    ? URL.createObjectURL(photo)
                    : (item.photo as string | undefined)
                }
                {...(!photo && !item.photo && item.firstName
                  ? stringAvatar(`${item.firstName} ${item.lastName}`, {
                      width: 88,
                      height: 88,
                      fontSize: 32,
                      border: `3px solid ${theme.palette.divider}`,
                    })
                  : {
                      sx: {
                        width: 88,
                        height: 88,
                        fontSize: 32,
                        border: `3px solid ${theme.palette.divider}`,
                      },
                    })}
              />
            </Badge>
          </Box>

          {/* Child details */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.general_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.first_name"))}
                    fullWidth
                    size="small"
                    {...register("firstName")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.last_name"))}
                    fullWidth
                    size="small"
                    {...register("lastName")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.gender"))}</InputLabel>
                    <Select
                      label={req(t("inputs.gender"))}
                      defaultValue={item.gender || "Male"}
                      {...register("gender")}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="Male">{t("gender.male")}</MenuItem>
                      <MenuItem value="Female">{t("gender.female")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.language}
                  >
                    <DesktopDatePicker
                      label={t("inputs.birthday")}
                      value={dob}
                      onChange={setDob}
                      inputFormat="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.parent"))}</InputLabel>
                    <Select
                      label={req(t("inputs.parent"))}
                      value={parentId}
                      onChange={(e) => setParentId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {parents.map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {p.firstName} {p.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.classroom"))}</InputLabel>
                    <Select
                      label={req(t("inputs.classroom"))}
                      value={classroomId}
                      onChange={(e) => setClassroomId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {classrooms.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("labels.attendance")}</InputLabel>
                    <Select
                      label={t("labels.attendance")}
                      defaultValue={String(item.attendanceMode ?? 0)}
                      {...register("attendanceMode" as any)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="0">{t("labels.full_time")}</MenuItem>
                      <MenuItem value="1">{t("labels.part_time")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Additional */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.additional_details")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label={t("inputs.ethnicity")}
                    fullWidth
                    size="small"
                    defaultValue={item.additionals?.ethnicity}
                    {...register("ethnicity" as any)}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={t("inputs.religion")}
                    fullWidth
                    size="small"
                    defaultValue={item.additionals?.religion}
                    {...register("religion" as any)}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("labels.language")}</InputLabel>
                    <Select
                      label={t("labels.language")}
                      defaultValue={
                        item.additionals?.languages?.[0] || "English"
                      }
                      {...register("languages" as any)}
                      sx={{ borderRadius: 2 }}
                    >
                      {languages.map(({ name, nativeName }) => (
                        <MenuItem key={name} value={name}>
                          {nativeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Contacts */}
          {[
            {
              label: "labels.father",
              prefix: "father",
              defaults: {
                firstName: item.fatherFirstName || "",
                lastName: item.fatherLastName || "",
                email: item.fatherEmail || "",
                mobile: item.fatherMobile || "",
              },
            },
            {
              label: "labels.mother",
              prefix: "mother",
              defaults: {
                firstName: item.motherFirstName || "",
                lastName: item.motherLastName || "",
                email: item.motherEmail || "",
                mobile: item.motherMobile || "",
              },
            },
            {
              label: "labels.other_contact",
              prefix: "third",
              defaults: {
                firstName: item.thirdFirstName || "",
                lastName: item.thirdLastName || "",
                email: item.thirdEmail || "",
                mobile: item.thirdMobile || "",
              },
            },
          ].map(({ label, prefix, defaults }) => (
            <Card
              key={prefix}
              variant="outlined"
              sx={{ borderRadius: 3, mb: 3 }}
            >
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  {t(label)}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.first_name")}
                      fullWidth
                      size="small"
                      defaultValue={defaults.firstName}
                      {...register(`${prefix}FirstName` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.last_name")}
                      fullWidth
                      size="small"
                      defaultValue={defaults.lastName}
                      {...register(`${prefix}LastName` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.email")}
                      fullWidth
                      size="small"
                      type="email"
                      defaultValue={defaults.email}
                      {...register(`${prefix}Email` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.mobile")}
                      fullWidth
                      size="small"
                      defaultValue={defaults.mobile}
                      {...register(`${prefix}Mobile` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {/* Dietary & allergies — nursery can edit and confirm */}
          <Box mb={3}>
            <ChildDietaryCard
              childId={(item as any).id}
              dietary={(item as any).dietary}
              canConfirm
            />
          </Box>

          <FormActions
            onCancel={() => navigate("/child")}
            submitting={submitting}
          />
        </form>
      </Container>
    </>
  );
};

export default EditChild;
